import { useLayoutEffect, type RefObject } from "react";

// One line, shrink to fit (founder, 1.5.2 b48 — talent names): the element
// keeps its CSS font size when the text fits its width; when it doesn't, the
// size steps down until it does, never below `min`. Past `min` the element's
// own CSS ends it in "…" (white-space: nowrap; overflow: hidden;
// text-overflow: ellipsis) — so the caller gives it a title with the full text.
//
// Re-fits when `watch` changes (the text, plus anything else that changes
// what or where it shows — a drawer opening), when the space around it
// changes width (rotation, a chip beside it) and once the fonts have loaded.
export function useFitText(ref: RefObject<HTMLElement | null>, watch: string, min: number) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // scrollWidth and clientWidth are whole pixels: 213.06px of text in a
    // 213px box passed that check and still ended in "…" (Instagram handle,
    // v3 2026-10-30). So the text's exact width must fit the box's exact
    // inner width too.
    const px = (v: string) => parseFloat(v) || 0;
    const fits = () => {
      if (el.scrollWidth > el.clientWidth + 0.5) return false;
      const range = document.createRange();
      if (typeof range.getBoundingClientRect !== "function") return true; // (no layout: jsdom)
      range.selectNodeContents(el);
      const cs = getComputedStyle(el);
      const inner =
        el.getBoundingClientRect().width - px(cs.paddingLeft) - px(cs.paddingRight) - px(cs.borderLeftWidth) - px(cs.borderRightWidth);
      return range.getBoundingClientRect().width <= inner + 0.01;
    };
    const fit = () => {
      el.style.fontSize = "";
      if (fits()) return;
      const max = parseFloat(getComputedStyle(el).fontSize);
      // Halve the gap between a size that fits and one that doesn't until
      // they are a hair apart (a quarter pixel left a 30-letter handle 4 px
      // short of its margins in a wide font); keep the one that fits.
      let lo = min;
      let hi = max;
      while (hi - lo > 0.03) {
        const mid = (lo + hi) / 2;
        el.style.fontSize = `${mid}px`;
        if (fits()) lo = mid;
        else hi = mid;
      }
      el.style.fontSize = `${lo}px`;
    };
    fit();
    // Watch the width of the space the line lives in, not the line itself:
    // its own height changes as it shrinks.
    const box = el.parentElement || el;
    let lastWidth = box.clientWidth;
    const ro =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            if (box.clientWidth === lastWidth) return;
            lastWidth = box.clientWidth;
            fit();
          });
    ro?.observe(box);
    window.addEventListener("resize", fit);
    document.fonts?.ready.then(fit).catch(() => {});
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", fit);
    };
  }, [ref, watch, min]);
}
